import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Breadcrumbs,
  Link,
  Tooltip
} from '@mui/material';
import {
  Assessment,
  Person,
  LocationOn,
  Description,
  Hotel,
  Link as LinkIcon,
  CloudUpload,
  Download,
  InsertDriveFile,
  Home,
  Edit,
  CheckCircle,
  Warning,
  ArrowBack,
  Assignment,
  ContentCopy,
  Check,
  FileDownload
} from '@mui/icons-material';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useCurrentUser } from '../../hooks/useApi';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { TrendReportFileUpload } from '../../components/admin/TrendReportFileUpload';
import { TrendReportStatusDialog } from '../../components/admin/TrendReportStatusDialog';

interface TrendReportFile {
  id: number;
  fileName: string;
  originalName: string;
  fileSize: number;
  contentType: string;
  uploadedBy: string;
  uploadedAt: string;
}

interface TrendReportDetail {
  id: number;
  stripe_session_id: string;
  stripe_payment_intent_id: string | null;
  product_id: string;
  price_id: string;
  amount: number;
  currency: string;
  status: 'purchased' | 'submitted' | 'needs_adjustments' | 'completed' | 'cancelled';
  report_url: string | null;
  notes: string | null;
  hotel_list: string[] | string | null;
  purchase_date: string;
  submitted_date: string | null;
  completed_date: string | null;
  metadata: any;
  created_at: string;
  updated_at: string;
  user: {
    id: string;
    name: string;
    email: string;
    last_login_at: string | null;
    is_active: boolean;
  };
  market: {
    id: number;
    name: string;
    full_name: string;
    description: string | null;
    is_active: boolean;
    country: {
      id: number;
      name: string;
      code: string;
      continent: {
        id: number;
        name: string;
      };
    };
  };
}

export const TrendReportDetail: React.FC = () => {
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [copiedInstructionId, setCopiedInstructionId] = useState<number | null>(null);

  // Check if MSAL is still initializing
  const isMSALBusy = inProgress !== InteractionStatus.None;
  
  // Check if we're authenticated but user data hasn't loaded yet
  const isWaitingForUserData = accounts.length > 0 && (isLoadingUser || !user);
  
  // Show loading spinner while authentication or user data is loading
  if (isMSALBusy || isWaitingForUserData) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Only redirect if we're sure the user has loaded and doesn't have permission
  // If there are no accounts, ProtectedRoute will handle the redirect
  if (accounts.length > 0 && user && !user.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }
  
  // If we have accounts but no user data, something went wrong
  if (accounts.length > 0 && !user) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">
            Failed to load user permissions. Please refresh the page or contact support.
          </Alert>
        </Box>
      </Container>
    );
  }

  // Fetch Custom Comp Set Report details from API
  const { data: reportData, isLoading, error } = useQuery({
    queryKey: ['trendReport', id],
    queryFn: async (): Promise<TrendReportDetail> => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/trend-report/${id}`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch Custom Comp Set Report details');
      }
      const data = await response.json();
      return data.trendReport;
    },
    enabled: !!user && !!id
  });

  // Fetch Custom Comp Set Report files
  const { data: filesData, isLoading: filesLoading, refetch: refetchFiles } = useQuery({
    queryKey: ['trendReportFiles', id],
    queryFn: async (): Promise<{ files: TrendReportFile[] }> => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/trend-report/${id}/files`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch Custom Comp Set Report files');
      }
      return response.json();
    },
    enabled: !!user && !!id && !!reportData
  });

  // Fetch Custom Comp Set Report special instructions
  const { data: instructionsData, isLoading: instructionsLoading, error: instructionsError } = useQuery({
    queryKey: ['trendReportInstructions', id],
    queryFn: async () => {
      console.log(`Fetching instructions for report ID: ${id}`);
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/trend-report/${id}/instructions`);
      if (!response || !response.ok) {
        console.error('Failed to fetch instructions:', response?.status);
        throw new Error('Failed to fetch Custom Comp Set Report instructions');
      }
      const data = await response.json();
      console.log('Instructions data received:', data);
      return data.data;
    },
    enabled: !!user && !!id && !!reportData
  });

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100); // Convert cents to dollars
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleCopyInstruction = async (instructionId: number, instructionText: string) => {
    try {
      await navigator.clipboard.writeText(instructionText);
      setCopiedInstructionId(instructionId);
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedInstructionId(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleExportCSV = () => {
    if (!instructionsData || instructionsData.length === 0) {
      return;
    }

    // Prepare CSV content
    const headers = ['Instruction Type', 'Description', 'Instructions'];
    const rows = instructionsData.map((instruction: any) => [
      instruction.instruction_type_name || '',
      instruction.instruction_type_description || '',
      instruction.instruction || ''
    ]);

    // Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map((row: string[]) =>
        row.map((cell: string) => {
          // Escape quotes and wrap in quotes if contains comma, newline, or quotes
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `trend_report_${id}_instructions.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileDownload = async (fileId: number, originalName: string) => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/admin/trend-report-file/${fileId}/download-url`
      );
      
      if (!response || !response.ok) {
        throw new Error('Failed to generate download URL');
      }

      const data = await response.json();
      
      // Open the download URL in a new tab
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleUploadComplete = () => {
    refetchFiles();
    setUploadDialogOpen(false);
  };

  const handleStatusUpdated = () => {
    // Invalidate both the current Custom Comp Set Report and the list of Custom Comp Set Reports
    queryClient.invalidateQueries({ queryKey: ['trendReport', id] });
    queryClient.invalidateQueries({ queryKey: ['trendReports'] });
    setStatusDialogOpen(false);
    // Redirect to admin trend reports list
    navigate('/admin/hub/marketcompass/reports');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'submitted':
        return 'info';
      case 'needs_adjustments':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'purchased':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'purchased':
        return 'Awaiting Submission';
      case 'submitted':
        return 'Under Review';
      case 'needs_adjustments':
        return 'Needs Adjustments';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !reportData) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">
            Failed to load Custom Comp Set Report details. Please try again later.
          </Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/hub/marketcompass/reports')}
            sx={{ mt: 2 }}
          >
            Back to Custom Comp Set Reports
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Link
            color="inherit"
            href="/admin/hub/marketcompass/reports"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub/marketcompass/reports');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Custom Comp Set Reports
          </Link>
          <Typography color="text.primary">Custom Comp Set Report #{reportData.id}</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Assessment sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                Custom Comp Set Report #{reportData.id}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={getStatusLabel(reportData.status)}
                color={reportData.status === 'purchased' ? 'default' : getStatusColor(reportData.status)}
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  padding: '6px 12px',
                  ...(reportData.status === 'purchased' && {
                    backgroundColor: 'rgb(232, 125, 30)',
                    color: 'white'
                  })
                }}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<Edit />}
                onClick={() => setStatusDialogOpen(true)}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                Change Status
              </Button>
            </Box>
          </Box>
          <Typography variant="body1" color="text.secondary">
            {reportData.market.name}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Customer Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Person color="primary" />
                  <Typography variant="h6">Customer Information</Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Name"
                      secondary={reportData.user.name || 'Not provided'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Email"
                      secondary={reportData.user.email}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Account Status"
                      secondary={
                        <Chip
                          label={reportData.user.is_active ? 'Active' : 'Inactive'}
                          color={reportData.user.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Last Login"
                      secondary={formatDate(reportData.user.last_login_at)}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Market Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocationOn color="primary" />
                  <Typography variant="h6">Market Information</Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Market"
                      secondary={reportData.market.full_name || reportData.market.name}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Country"
                      secondary={`${reportData.market.country.name} (${reportData.market.country.code})`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Continent"
                      secondary={reportData.market.country.continent.name}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Market Status"
                      secondary={
                        <Chip
                          label={reportData.market.is_active ? 'Active' : 'Inactive'}
                          color={reportData.market.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>


          {/* Report Details */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Description color="primary" />
                  <Typography variant="h6">Report Details</Typography>
                </Box>
                
                {/* Timeline Information */}
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Purchase Date
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(reportData.purchase_date)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Submitted Date
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(reportData.submitted_date)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Completed Date
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(reportData.completed_date)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Last Updated
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(reportData.updated_at)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ mb: 3 }} />
                
                {reportData.notes && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Admin Comments
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'warning.50', borderLeft: '4px solid', borderLeftColor: 'warning.main' }}>
                      <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                        {reportData.notes}
                      </Typography>
                    </Paper>
                  </Box>
                )}
                
                {reportData.report_url && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Report URL
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinkIcon fontSize="small" color="action" />
                      <Typography
                        component="a"
                        href={reportData.report_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {reportData.report_url}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {reportData.notes && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Notes
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                        {reportData.notes}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {reportData.hotel_list && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Hotel fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Hotel List
                      </Typography>
                    </Box>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      {Array.isArray(reportData.hotel_list) ? (
                        <List dense>
                          {reportData.hotel_list.map((hotel, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={hotel} />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                          {typeof reportData.hotel_list === 'string'
                            ? reportData.hotel_list
                            : JSON.stringify(reportData.hotel_list, null, 2)}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                )}

                {/* Special Instructions Section */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Assignment fontSize="small" color="action" />
                      <Typography variant="subtitle2" color="text.secondary">
                        Special Instructions
                      </Typography>
                    </Box>
                    {instructionsData && instructionsData.length > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileDownload />}
                        onClick={handleExportCSV}
                        sx={{ textTransform: 'none' }}
                      >
                        Export to CSV
                      </Button>
                    )}
                  </Box>
                  {instructionsError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      Failed to load special instructions: {(instructionsError as Error).message}
                    </Alert>
                  ) : instructionsLoading ? (
                    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Loading special instructions...
                      </Typography>
                    </Paper>
                  ) : instructionsData && instructionsData.length > 0 ? (
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Instruction Type</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Instructions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {instructionsData.map((instruction: any) => (
                            <TableRow key={instruction.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                              <TableCell sx={{ verticalAlign: 'top', width: '30%' }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    {instruction.instruction_type_name}
                                  </Typography>
                                  {instruction.instruction_type_description && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      {instruction.instruction_type_description}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <Tooltip
                                    title={copiedInstructionId === instruction.id ? 'Copied!' : 'Copy to clipboard'}
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCopyInstruction(instruction.id, instruction.instruction)}
                                      sx={{
                                        padding: '4px',
                                        color: copiedInstructionId === instruction.id ? 'success.main' : 'action.active',
                                        '&:hover': {
                                          backgroundColor: 'action.hover'
                                        }
                                      }}
                                    >
                                      {copiedInstructionId === instruction.id ? (
                                        <Check fontSize="small" />
                                      ) : (
                                        <ContentCopy fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', flex: 1 }}>
                                    {instruction.instruction}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        No special instructions have been added for this report.
                      </Typography>
                    </Paper>
                  )}
                </Box>

              </CardContent>
            </Card>
          </Grid>

          {/* Uploaded Documents - only show if status is not 'purchased' */}
          {reportData.status !== 'purchased' && (
            <Grid item xs={12}>
              <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InsertDriveFile color="primary" />
                    <Typography variant="h6">Uploaded Documents</Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<CloudUpload />}
                    onClick={() => setUploadDialogOpen(true)}
                    size="small"
                  >
                    Upload New File
                  </Button>
                </Box>

                {filesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : filesData?.files && filesData.files.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>File Name</TableCell>
                          <TableCell>Size</TableCell>
                          <TableCell>Upload Date</TableCell>
                          <TableCell>Uploaded By</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filesData.files.map((file) => {
                          const isHtmlFile = file.contentType === 'text/html' ||
                            /\.html?$/i.test(file.originalName);
                          return (
                          <TableRow key={file.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <InsertDriveFile fontSize="small" color="action" />
                                {isHtmlFile ? (
                                  <Typography
                                    variant="body2"
                                    component="a"
                                    href="#"
                                    onClick={async (e: React.MouseEvent) => {
                                      e.preventDefault();
                                      await handleFileDownload(file.id, file.originalName);
                                    }}
                                    sx={{
                                      color: 'primary.main',
                                      textDecoration: 'none',
                                      cursor: 'pointer',
                                      '&:hover': { textDecoration: 'underline' }
                                    }}
                                  >
                                    {file.originalName}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2">{file.originalName}</Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{formatFileSize(file.fileSize)}</TableCell>
                            <TableCell>{formatDate(file.uploadedAt)}</TableCell>
                            <TableCell>{file.uploadedBy}</TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleFileDownload(file.id, file.originalName)}
                                title="Download"
                              >
                                <Download />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <InsertDriveFile sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      No documents uploaded yet
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      onClick={() => setUploadDialogOpen(true)}
                      sx={{ mt: 2 }}
                    >
                      Upload First Document
                    </Button>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>
          )}
        </Grid>

        {/* File Upload Dialog */}
        {reportData && (
          <TrendReportFileUpload
            open={uploadDialogOpen}
            onClose={() => setUploadDialogOpen(false)}
            trendReportId={reportData.id}
            marketName={reportData.market.name}
            onUploadComplete={handleUploadComplete}
          />
        )}

        {/* Status Change Dialog */}
        {reportData && (
          <TrendReportStatusDialog
            open={statusDialogOpen}
            onClose={() => setStatusDialogOpen(false)}
            trendReportId={reportData.id}
            currentStatus={reportData.status}
            marketName={reportData.market.name}
            currentNotes={reportData.notes}
            onStatusUpdated={handleStatusUpdated}
          />
        )}
      </Box>
    </Container>
  );
};